import React from 'react'
import { X, Bug, AlertCircle } from 'lucide-react'
import { useDebugMode } from '@/lib/hooks/useDebugMode'
import { MemberSelector } from '@/components/layout/MemberSelector'
import { useAuth } from '@/lib/AuthContext'

export const DebugPanel: React.FC = () => {
  const { isDebugMode, setIsDebugMode } = useDebugMode()
  const { user } = useAuth()
  const [showToast, setShowToast] = React.useState(false)

  const isMemberfyUser = user?.email?.endsWith('@memberfy.net')

  React.useEffect(() => {
    if (isDebugMode && !showToast) {
      setShowToast(true)
      setTimeout(() => setShowToast(false), 2000)
    }
  }, [isDebugMode])

  if (!isMemberfyUser) {
    return null
  }

  /* A staff tool (every @memberfy.net account), not part of the product: no
     floating button over the page's actions. It opens with Ctrl+Shift+D or
     `?debug=true`. */
  if (!isDebugMode) {
    return (
      <>
        {showToast && (
          <div className="fixed top-4 right-4 z-[100] bg-white px-4 py-2 rounded-lg shadow-lg border-2 border-primary animate-in slide-in-from-right">
            <div className="text-sm font-medium text-gray-900">
              Debug mode activated
            </div>
            <div className="text-xs text-gray-500">
              Press Ctrl+Shift+D to toggle
            </div>
          </div>
        )}
      </>
    )
  }

  return (
    <div className="fixed bottom-20 right-4 lg:bottom-4 lg:left-[22rem] lg:right-auto z-[100] w-80 bg-white rounded-xl shadow-lg border-2 border-gray-200 p-4 animate-in slide-in-from-right">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Bug className="h-5 w-5 text-primary" />
          <h3 className="text-base font-semibold text-gray-900">Debug Panel</h3>
        </div>
        <button
          onClick={() => setIsDebugMode(false)}
          className="h-8 w-8 rounded-lg hover:bg-gray-100 transition-colors flex items-center justify-center"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="space-y-4">
        {user?.isSuperAdmin && (
          <div className="pb-4 border-b">
            <div className="text-sm font-medium text-gray-700 mb-3">Member Selector</div>
            <MemberSelector />
          </div>
        )}

        <div className="pt-4 border-t space-y-2">
          <div className="text-sm font-medium text-gray-700 mb-2">Sentry Testing</div>
          <button
            onClick={() => {
              throw new Error('Test error from Debug Panel - Sentry is working!')
            }}
            className="w-full px-3 py-2 text-sm text-primary-600 hover:bg-primary-50 rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            <AlertCircle className="h-4 w-4" />
            Throw Test Error
          </button>
          <div className="text-xs text-gray-500">
            Triggers an error to test Sentry integration
          </div>
        </div>

        <div className="text-xs text-gray-500 text-center pt-2 border-t">
          Press <kbd className="px-1 py-0.5 bg-gray-200 rounded text-xs font-mono">Ctrl+Shift+D</kbd> to toggle
        </div>
      </div>
    </div>
  )
}
